import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ethers } from 'ethers';
import { useWriteContract } from 'wagmi';
import { useAppKitAccount } from '@reown/appkit/react';
import { provider } from '../../.config';
import BullsEyeABI from '../../abi/BullsEye.json';
import ERC20ABI from '../../abi/ERC20_ABI.json';
import {
  bullsEyeAddress,
  usdtAddress,
  wbtcAddress,
  USDT_DECIMALS,
  WBTC_DECIMALS,
  PRICE_DECIMALS,
  TIERS,
  buildTiers,
} from './config';
import { setChainOffset } from './components';

const REFRESH_MS = 30000;
const ZERO = '0x0000000000000000000000000000000000000000';
/** Bets live in an unbounded array; read them in chunks, capped. */
const BET_CHUNK = 12;
const BET_LIMIT = 240;

const toNum = (value, decimals) =>
  parseFloat(ethers.utils.formatUnits(value.toString(), decimals));

const emptyRound = { pool: 0, startTime: 0, endTime: 0, resolved: false };

/**
 * Resolves to `fallback` instead of throwing, so one failing read (an older
 * deployment, a flaky RPC) never blanks the whole page.
 */
const settle = (promise, fallback = null) =>
  Promise.resolve(promise)
    .then((value) => value)
    .catch(() => fallback);

/**
 * Everything the BullsEye pages read from chain.
 *
 * Reads use the shared ethers provider, like every other Luckblocks page;
 * writes go through wagmi so they use the connected wallet.
 */
export function useBullsEye() {
  const { address, isConnected } = useAppKitAccount();
  const { writeContractAsync } = useWriteContract();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [liveRound, setLiveRound] = useState(emptyRound);
  const [nextRound, setNextRound] = useState(emptyRound);
  const [liveRoundId, setLiveRoundId] = useState(0);
  const [nextRoundId, setNextRoundId] = useState(0);

  const [btcPrice, setBtcPrice] = useState(0);
  const [roundedPrice, setRoundedPrice] = useState(0);
  const [jackpot, setJackpot] = useState(0);
  const [pool, setPool] = useState(0);

  const [betAmount, setBetAmount] = useState(0);
  const [roundDuration, setRoundDuration] = useState(0);
  const [bonusFirstN, setBonusFirstN] = useState(0);
  const [bonusMultiplier, setBonusMultiplier] = useState(0);
  const [fees, setFees] = useState(null);
  const [tiers, setTiers] = useState(TIERS);

  const [prevStats, setPrevStats] = useState(null);
  const [prevWinners, setPrevWinners] = useState([]);
  const [myLiveBets, setMyLiveBets] = useState([]);
  const [myNextBets, setMyNextBets] = useState([]);
  const [liveBetCount, setLiveBetCount] = useState(0);

  const [allowance, setAllowance] = useState(0);
  const [usdtBalance, setUsdtBalance] = useState(0);
  const [wbtcBalance, setWbtcBalance] = useState(0);

  const [referral, setReferral] = useState({
    rate: 0,
    count: 0,
    referrer: null,
    firstTime: false,
    earned: null,
  });
  const [month, setMonth] = useState(0);
  const [monthlyPot, setMonthlyPot] = useState(0);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const game = useMemo(
    () => new ethers.Contract(bullsEyeAddress, BullsEyeABI.abi, provider),
    []
  );

  /**
   * Walks a round bet array. `bets(roundId, i)` reverts once `i` runs past the
   * end, which is how the length is discovered, since the contract exposes no count.
   */
  const readRoundBets = useCallback(
    async (roundId) => {
      if (!roundId) return [];
      const all = [];
      for (let start = 0; start < BET_LIMIT; start += BET_CHUNK) {
        const chunk = await Promise.all(
          Array.from({ length: BET_CHUNK }, (slot, i) => settle(game.bets(roundId, start + i)))
        );
        for (let i = 0; i < chunk.length; i += 1) {
          if (!chunk[i]) return all;
          all.push({
            index: start + i,
            player: chunk[i][0],
            price: Number(chunk[i][1].toString()),
          });
        }
      }
      return all;
    },
    [game]
  );

  const refresh = useCallback(async () => {
    try {
      const [live, next, liveId, nextId, price, rounded, jack, currentPool] = await Promise.all([
        settle(game.getLiveRound()),
        settle(game.getNextRound()),
        settle(game.getLiveRoundId()),
        settle(game.getNextRoundId()),
        settle(game.getBTCPrice()),
        settle(game.getRoundedPrice()),
        settle(game.getCurrentJackpot()),
        settle(game.getCurrentPool()),
      ]);

      if (!mounted.current) return;

      const block = await settle(provider.getBlock('latest'));
      if (block) setChainOffset(Number(block.timestamp) * 1000 - Date.now());

      if (live) {
        setLiveRound({
          pool: toNum(live.usdtPool || 0, USDT_DECIMALS),
          startTime: Number(live.startTime) * 1000,
          endTime: Number(live.endTime) * 1000,
          resolved: Boolean(live.resolved),
        });
      }
      if (next) {
        setNextRound({
          pool: toNum(next.usdtPool || 0, USDT_DECIMALS),
          startTime: Number(next.startTime) * 1000,
          endTime: Number(next.endTime) * 1000,
          resolved: Boolean(next.resolved),
        });
      }

      const liveIdNum = liveId ? Number(liveId.toString()) : 0;
      const nextIdNum = nextId ? Number(nextId.toString()) : 0;
      setLiveRoundId(liveIdNum);
      setNextRoundId(nextIdNum);

      if (price) setBtcPrice(toNum(price, PRICE_DECIMALS));
      if (rounded) setRoundedPrice(Number(rounded.toString()));
      if (jack) setJackpot(toNum(jack, WBTC_DECIMALS));
      if (currentPool) setPool(toNum(currentPool, USDT_DECIMALS));

      // Rules are read, never hardcoded, so the page cannot drift away from the
      // parameters the contract is actually running.
      const [amount, duration, house, jackFee, poolCut, monthlyFee, firstN, multiplier, tierConfig, stakeFee, lm, nodeFee] =
        await Promise.all([
          settle(game.BET_AMOUNT()),
          settle(game.ROUND_DURATION()),
          settle(game.HOUSE_FEE()),
          settle(game.JACKPOT_FEE()),
          settle(game.POOL_CONTRIBUTION()),
          settle(game.MONTHLY_LEADERBOARD_FEE()),
          settle(game.BONUS_FIRST_N()),
          settle(game.BONUS_MULTIPLIER()),
          settle(game.getTierConfig()),
          settle(game.STAKE_FEE()),
          settle(game.liquidityMining()),
          settle(game.NODE_FEE()),
        ]);

      if (!mounted.current) return;

      if (amount) setBetAmount(toNum(amount, USDT_DECIMALS));
      if (duration) setRoundDuration(Number(duration.toString()));
      if (firstN) setBonusFirstN(Number(firstN.toString()));
      if (multiplier) setBonusMultiplier(Number(multiplier.toString()) - 100);
      if (tierConfig) {
        setTiers(buildTiers(tierConfig[0].map(Number), tierConfig[1].map(Number)));
      }
      // the liquidity-mining share is paid out of the house fee
      const staking = stakeFee && lm && lm !== ethers.constants.AddressZero ? Number(stakeFee.toString()) / 100 : 0;
      const pct = (value) => (value ? Number(value.toString()) / 100 : null);
      const cuts = [house, jackFee, poolCut, monthlyFee, nodeFee].map(pct);
      // the round pot is whatever the fees leave (BullsEye._splitBet)
      const round = cuts.every((value) => value !== null) ? Math.round(10000 - cuts.reduce((sum, value) => sum + value * 100, 0)) / 100 : null;
      setFees({
        round,
        house: house ? pct(house) - staking : null,
        staking: staking || null,
        jackpot: pct(jackFee),
        rollover: pct(poolCut),
        node: pct(nodeFee),
        monthly: pct(monthlyFee),
      });

      const [prev, currentMonth, monthlyInfo] = await Promise.all([
        settle(game.getPreviousRoundStats()),
        settle(game.getCurrentMonth()),
        settle(game.getMonthlyInfo()),
      ]);

      if (!mounted.current) return;

      let prevId = 0;
      if (prev) {
        prevId = Number(prev[0].toString());
        setPrevStats({
          roundId: prevId,
          price: Number(prev[1].toString()),
          winnersCount: Array.from(prev[2]).map((count) => Number(count.toString())),
        });
      }
      if (currentMonth) setMonth(Number(currentMonth.toString()));
      if (monthlyInfo) setMonthlyPot(toNum(monthlyInfo[0], WBTC_DECIMALS));

      const winners = prevId ? await settle(game.getRoundWinners(prevId), []) : [];
      if (!mounted.current) return;
      setPrevWinners(
        (winners || []).map((winner) => ({
          player: winner.player,
          price: Number(winner.predictedPrice.toString()),
          usdt: toNum(winner.usdtPrize, USDT_DECIMALS),
          wbtc: toNum(winner.wbtcPrize, WBTC_DECIMALS),
          tier: Number(winner.tier.toString()),
        }))
      );

      // The bet array doubles as the counter for the first-N bonus.
      const liveBets = await readRoundBets(liveIdNum);
      if (!mounted.current) return;
      setLiveBetCount(liveBets.length);

      if (address) {
        const lower = address.toLowerCase();
        const nextBets =
          nextIdNum && nextIdNum !== liveIdNum ? await readRoundBets(nextIdNum) : liveBets;

        if (!mounted.current) return;
        setMyLiveBets(
          liveBets.filter((bet) => bet.player.toLowerCase() === lower).map((bet) => bet.price)
        );
        setMyNextBets(
          nextBets.filter((bet) => bet.player.toLowerCase() === lower).map((bet) => bet.price)
        );

        const usdt = new ethers.Contract(usdtAddress, ERC20ABI.abi, provider);
        const wbtc = new ethers.Contract(wbtcAddress, ERC20ABI.abi, provider);

        const [allow, balanceUsdt, balanceWbtc, rate, count, referrer, first, earned] =
          await Promise.all([
            settle(usdt.allowance(address, bullsEyeAddress)),
            settle(usdt.balanceOf(address)),
            settle(wbtc.balanceOf(address)),
            settle(game.getReferralRate(address)),
            settle(game.referralCount(address)),
            settle(game.referrers(address)),
            settle(game.firstTimeUser(address)),
            settle(game.totalReferralEarned(address)),
          ]);

        if (!mounted.current) return;

        if (allow) setAllowance(toNum(allow, USDT_DECIMALS));
        if (balanceUsdt) setUsdtBalance(toNum(balanceUsdt, USDT_DECIMALS));
        if (balanceWbtc) setWbtcBalance(toNum(balanceWbtc, WBTC_DECIMALS));

        setReferral({
          rate: rate ? Number(rate.toString()) / 100 : 0,
          count: count ? Number(count.toString()) : 0,
          referrer: referrer && referrer !== ZERO ? referrer : null,
          firstTime: Boolean(first),
          // Older deployments lack it, so the card hides the row instead of
          // publishing a zero it cannot vouch for.
          earned: earned ? toNum(earned, USDT_DECIMALS) : null,
        });
      } else {
        setMyLiveBets([]);
        setMyNextBets([]);
      }

      setError(null);
    } catch (err) {
      console.error('[BullsEye] read failed', err);
      if (mounted.current) setError('Could not reach the game contract.');
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [game, address, readRoundBets]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(id);
  }, [refresh]);

  /**
   * `writeContractAsync` resolves as soon as the wallet broadcasts, so refreshing
   * immediately would re-read the pre-transaction state and leave the UI a poll
   * behind. Wait for the receipt first.
   */
  const settleWrite = useCallback(
    async (hash) => {
      if (hash) await provider.waitForTransaction(hash).catch(() => null);
      await refresh();
      return hash;
    },
    [refresh]
  );

  const approveUsdt = useCallback(
    async (amount) => {
      const value = ethers.utils.parseUnits(String(amount), USDT_DECIMALS);
      const hash = await writeContractAsync({
        address: usdtAddress,
        abi: ERC20ABI.abi,
        functionName: 'approve',
        args: [bullsEyeAddress, value],
      });
      return settleWrite(hash);
    },
    [writeContractAsync, settleWrite]
  );

  const placeBet = useCallback(
    async (predictions, referrer) => {
      const hash = await writeContractAsync({
        address: bullsEyeAddress,
        abi: BullsEyeABI.abi,
        functionName: 'placeBet',
        args: [predictions.map((value) => Math.round(value)), referrer || ZERO],
      });
      return settleWrite(hash);
    },
    [writeContractAsync, settleWrite]
  );

  return {
    address,
    isConnected,
    loading,
    error,
    liveRound,
    nextRound,
    liveRoundId,
    nextRoundId,
    btcPrice,
    roundedPrice,
    jackpot,
    pool,
    betAmount,
    roundDuration,
    bonusFirstN,
    bonusMultiplier,
    fees,
    prevStats,
    prevWinners,
    myLiveBets,
    myNextBets,
    liveBetCount,
    allowance,
    usdtBalance,
    wbtcBalance,
    referral,
    tiers,
    month,
    monthlyPot,
    approveUsdt,
    placeBet,
    refresh,
    contract: game,
    readRoundBets,
  };
}

export default useBullsEye;
